import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';

/**
 * DEV-3224 (TDT-3484, TDT-3487) — rapor çıktısını GERÇEK .xlsx olarak indirir.
 *
 * Eski yol `vue-excel-export` idi: bir HTML tablosu üretip `.xls` adıyla,
 * `application/vnd.ms-excel` MIME'ıyla indiriyordu. İki kusur doğuruyordu:
 *
 *  1. Excel, uzantı ile içeriğin uyuşmadığını görüp "biçim ve uzantı
 *     eşleşmiyor / dosya bozuk olabilir" uyarısı veriyordu (TDT-3484).
 *  2. HTML hücresinde tip bilgisi yok; Excel her hücreyi kendi yereline göre
 *     yorumluyordu. Türkçe yerelde nokta tarih ayırıcısı olduğu için "10.75"
 *     1 Ekim 1975'e dönüşüp "Eki.75" görünüyordu (TDT-3487). Kodda buna karşı
 *     iki elle yama vardı (parasal kodlarda nokta->virgül, takip tipinde sıfır
 *     genişlikli boşluk); liste her yeni alanda güncellenmeyi unutulduğu için
 *     Masraf 2 sütunu açıkta kalmıştı.
 *
 * Burada hücreler AÇIKÇA tiplenir: parasal alanlar sayı (Excel'de toplanır ve
 * sayısal sıralanır), diğerleri metin. Tarih tuzağı yapısal olarak imkânsız
 * hâle gelir — Excel bir metin hücresini yorumlamaz.
 */

/** Bir rapor sütunu; `sayisal`, katalogdaki kosul_input === 'tutar' demektir. */
export interface RaporExcelKolon {
  baslik: string;
  kod: string;
  sayisal: boolean;
}

/** Excel'in para gösterimi: binlik ayraçlı, iki ondalık. Yerel ayara uyar. */
const PARA_BICIMI = '#,##0.00';

/**
 * Yalnız düz ondalık sayıyı (API'nin döndürdüğü biçim: "96270.00", "-12.5")
 * sayıya çevirir. "2010/1200", "1.234,56" veya boş değer metin kalır —
 * geniş bir sayı sezgisi TC kimlik / telefon gibi sütunları da sayıya
 * çevirip baştaki sıfırı düşürür ya da bilimsel gösterime kaydırırdı.
 */
const DUZ_ONDALIK = /^-?\d+(\.\d+)?$/;

function hucreDegeri(ham: unknown, sayisal: boolean): string | number {
  if (ham === null || ham === undefined) {
    return '';
  }
  // API'den SAYI tipinde gelen değer sayı kalır. Veritabanı tipi burada kanonik
  // sinyaldir: tam sayı kolonlar (kart no, dosya no) JSON'a number olarak,
  // metin kolonlar (TC kimlik, telefon, esas no) string olarak iner. Bu ayrım
  // olmadan kart no Excel'de metin yazılır ve sayısal sıralamada "10" < "9"
  // olurdu — eski HTML yolunda Excel onu sayı yorumladığı için bu bir gerileme
  // olurdu. Metin kolonları yine metin kalır, bilimsel gösterime kaymaz.
  if (typeof ham === 'number') {
    return Number.isFinite(ham) ? ham : '';
  }
  const metin = String(ham).trim();
  if (metin === '') {
    return '';
  }
  if (sayisal && DUZ_ONDALIK.test(metin)) {
    const n = Number(metin);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return metin;
}

/** Windows ve macOS'ta dosya adında kullanılamayan karakterler. */
const GECERSIZ_AD_KARAKTERLERI = /[\\/:*?"<>|]/g;

export function raporExcelDosyaAdi(raporAdi: string): string {
  const temiz = (raporAdi || 'rapor').replace(GECERSIZ_AD_KARAKTERLERI, '-').trim();
  const ad = temiz || 'rapor';
  return /\.xlsx$/i.test(ad) ? ad : `${ad}.xlsx`;
}

export interface RaporExcelIndirSecenekleri {
  kolonlar: RaporExcelKolon[];
  satirlar: Array<Record<string, unknown>>;
  raporAdi: string;
  sayfaAdi?: string;
}

/**
 * @returns indirme başlatıldıysa true; sütun yoksa false
 */
export function raporExcelIndir(secenekler: RaporExcelIndirSecenekleri): boolean {
  const {kolonlar, satirlar, raporAdi, sayfaAdi = 'Rapor'} = secenekler;
  if (!kolonlar.length) {
    return false;
  }

  const basliklar = kolonlar.map(k => k.baslik);
  const govde = satirlar.map(satir => kolonlar.map(k => hucreDegeri(satir[k.kod], k.sayisal)));

  const worksheet = XLSX.utils.aoa_to_sheet([basliklar, ...govde]);

  // Sayı hücrelerine para biçimi ver; aoa_to_sheet tipi JS tipinden atadığı
  // için burada yalnız biçim (z) eklenir, tip değiştirilmez.
  kolonlar.forEach((kolon, sutun) => {
    if (!kolon.sayisal) {
      return;
    }
    for (let satir = 1; satir <= govde.length; satir++) {
      const hucre = worksheet[XLSX.utils.encode_cell({r: satir, c: sutun})];
      if (hucre && hucre.t === 'n') {
        hucre.z = PARA_BICIMI;
      }
    }
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sayfaAdi.slice(0, 31));
  const buffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, raporExcelDosyaAdi(raporAdi));
  return true;
}
